import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiEssentials } from '../../shared/hooks/useApiEssentials';
import { get, put } from '../../shared/api/apiService';
import { Credentials, CredentialsResponse } from '@app/shared/types';

export function useCredentials(userID?: string) {
  const { apiUrl, getIdToken, isSignedIn } = useApiEssentials();

  return useQuery<CredentialsResponse, Error>({
    queryKey: ['credentials', userID],
    queryFn: async () => {
      const idToken = await getIdToken();
      return get<CredentialsResponse>(
        `/client_credentials/${userID ?? 'me'}`,
        apiUrl,
        idToken,
      );
    },
    enabled: Boolean(apiUrl && isSignedIn),
    retry: false,
  });
}

export function useUpdateCredentials(userID?: string) {
  const { apiUrl, getIdToken } = useApiEssentials();
  const queryClient = useQueryClient();

  return useMutation<void, Error, Credentials[]>({
    mutationFn: async (newCredentials: Credentials[]) => {
      const idToken = await getIdToken();
      return put<void>(
        `/client_credentials/${userID}`,
        newCredentials,
        apiUrl,
        idToken,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['credentials', userID],
      });
    },
    mutationKey: ['credentials', userID],
  });
}
